import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Button from '../components/Button';
import MoodboardItem from '../components/MoodboardItem';
import axios from 'axios';

const MoodboardDetail = () => {
  const { moodboardId } = useParams();
  const [moodboard, setMoodboard] = useState(null);
  const [items, setItems] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [itemBeingEdited, setItemBeingEdited] = useState(null);
  
  // State for associated entity (album or song) if any
  const [associatedEntity, setAssociatedEntity] = useState(null);
  const [associatedAlbum, setAssociatedAlbum] = useState(null);
  const [songs, setSongs] = useState([]);
  
  const API_URL = 'https://hqln2txwrk.execute-api.us-east-1.amazonaws.com/dev';

  useEffect(() => {
    // Fetch moodboard data from API
    const fetchMoodboard = async () => {
      try {
        const response = await axios.get(`${API_URL}/moodboards/${moodboardId}`);
        const foundMoodboard = response.data;
        setMoodboard(foundMoodboard);
        
        // If moodboard has an albumId, fetch the associated album
        if (foundMoodboard.albumId) {
          const albumResponse = await axios.get(`${API_URL}/albums/${foundMoodboard.albumId}`);
          setAssociatedAlbum(albumResponse.data);
          setAssociatedEntity(albumResponse.data);
        }
        
        // Fetch songs for this moodboard if available
        const songsResponse = await axios.get(`${API_URL}/songs`);
        setSongs(songsResponse.data.filter(song => song.albumId === foundMoodboard.albumId));
        
        // Set items from moodboard
        if (foundMoodboard.items) {
          if (foundMoodboard.itemOrder) {
            const sortedItems = [...foundMoodboard.items].sort((a, b) => {
              return foundMoodboard.itemOrder.indexOf(a.id) - 
                     foundMoodboard.itemOrder.indexOf(b.id);
            });
            setItems(sortedItems);
          } else {
            setItems(foundMoodboard.items);
          }
        }
      } catch (error) {
        console.error('Error fetching moodboard:', error);
      }
    };
    
    if (moodboardId) {
      fetchMoodboard();
    }
  }, [moodboardId]);
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const reorderedItems = Array.from(items);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    
    setItems(reorderedItems);
    
    // In a real app, this would update the backend
    console.log('New item order:', reorderedItems.map(item => item.itemId));
  };
  
  const handleEditItem = (item) => {
    setItemBeingEdited(item);
    setShowAddItemModal(true);
  };
  
  const handleDeleteItem = (item) => {
    if (window.confirm(`Are you sure you want to delete "${item.title || 'this item'}"?`)) {
      const updatedItems = items.filter(i => i.itemId !== item.itemId);
      setItems(updatedItems);
      
      // In a real app, this would update the backend
      console.log('Item deleted:', item.itemId);
    }
  };
  
  const handleAddItem = () => {
    setItemBeingEdited(null);
    setShowAddItemModal(true);
  };
  
  if (!moodboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading moodboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Link to="/moodboards" className="text-primary-600 hover:text-primary-700 mr-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">{moodboard.title}</h1>
        </div>
        
        <p className="text-gray-600 mb-4">{moodboard.description}</p>
        
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {associatedAlbum && (
            <Link 
              to={`/albums/${associatedAlbum.albumId}`}
              className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
              </svg>
              {associatedAlbum.title}
            </Link>
          )}
          
          {moodboard.tags && moodboard.tags.map(tag => (
            <span 
              key={tag}
              className="inline-block px-3 py-1 bg-gray-100 text-gray-800 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant={isEditMode ? "secondary" : "primary"}
            onClick={() => setIsEditMode(!isEditMode)}
          >
            {isEditMode ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Exit Edit Mode
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Moodboard
              </>
            )}
          </Button>
          
          {isEditMode && (
            <Button 
              variant="primary"
              onClick={handleAddItem}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Item
            </Button>
          )}
          
          <Button 
            variant="secondary"
            onClick={() => alert('Export functionality would be here')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Export
          </Button>
        </div>
      </div>
      
      {/* Moodboard Items */}
      {isEditMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="moodboard-items" direction="horizontal">
            {(provided) => (
              <div 
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              >
                {items.map((item, index) => (
                  <Draggable key={item.itemId} draggableId={item.itemId} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <MoodboardItem 
                          item={item} 
                          onEdit={handleEditItem}
                          onDelete={handleDeleteItem}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(item => (
            <MoodboardItem 
              key={item.itemId} 
              item={item} 
              onEdit={() => {}}
              onDelete={() => {}}
            />
          ))}
        </div>
      )}
      
      {/* Placeholder for Add/Edit Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">
              {itemBeingEdited ? 'Edit Item' : 'Add New Item'}
            </h3>
            <p className="mb-4 text-gray-600">
              This is a placeholder for the {itemBeingEdited ? 'edit' : 'add'} item form.
              In a real implementation, this would include fields for item type, title, content, tags, etc.
            </p>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="secondary"
                onClick={() => setShowAddItemModal(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="primary"
                onClick={() => {
                  alert(`Item would be ${itemBeingEdited ? 'updated' : 'added'} here`);
                  setShowAddItemModal(false);
                }}
              >
                {itemBeingEdited ? 'Update' : 'Add'} Item
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodboardDetail;
